import noop from 'lodash/noop';
import React, { PropsWithChildren, ReactElement } from 'react';
import { Spacing } from '../../design-system/spacing';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { styled } from '../../utils/styled';
import { Box } from '../Box';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { CloseIcon } from '../Icons/CloseIcon';
import {
  ContentWrapper,
  Puller,
  StyledButtonContent,
} from '../RightSideDrawer/RightSideDrawer.styles';
import { Stack } from '../Stack';
import { SwipeableDrawer } from '../SwipeableDrawer';

const StyledStack = styled(Stack)`
  padding: ${({ theme }) => theme.spacing(Spacing.Medium)};
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const StyledContainerPuller = styled(Stack)`
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(Spacing.Small)};
`;
const StyledSwipeableDrawer = styled(SwipeableDrawer)`
  .MuiDrawer-paper {
    border-top: 1px solid ${({ theme }) => theme.palette.neutral[200]};
    border-left: 1px solid ${({ theme }) => theme.palette.neutral[200]};
    box-shadow: none;
  }
`;

interface PermanentDrawerProps {
  readonly header?: ReactElement;
  readonly isOpen?: boolean;
  readonly noJustifyContent?: boolean;
  readonly actionButtons?: ReactElement;
  readonly handleClose: () => void;
  readonly handleOpen?: () => void;
  readonly onAnimationEnd?: () => void;
  readonly fullHeight?: boolean;
}

export function PermanentDrawer({
  isOpen,
  children,
  actionButtons,
  handleClose,
  onAnimationEnd,
  header,
  handleOpen = noop,
  noJustifyContent = false,
  fullHeight = false,
}: PropsWithChildren<PermanentDrawerProps>) {
  const { isMdUp } = useBreakpoints();
  return (
    <div>
      <StyledSwipeableDrawer
        anchor={isMdUp ? 'right' : 'bottom'}
        fullHeight={fullHeight}
        onAnimationEnd={onAnimationEnd}
        onClose={handleClose}
        onOpen={handleOpen}
        open={isOpen}
        variant={isMdUp ? 'persistent' : 'temporary'}
      >
        <StyledContainerPuller>
          {!isMdUp && <Puller />}
          {header ? (
            <StyledStack>
              <Box>{header}</Box>
              <Button
                color="neutral"
                onClick={handleClose}
                startIcon={
                  <CloseIcon aria-label="Close Drawer" title="Close Drawer" />
                }
                variant="text"
              />
            </StyledStack>
          ) : null}
        </StyledContainerPuller>
        <Divider noMargin />
        <ContentWrapper>{children}</ContentWrapper>
        {actionButtons ? (
          <>
            <Divider noMargin />
            <StyledButtonContent noJustifyContent={noJustifyContent}>
              {actionButtons}
            </StyledButtonContent>
          </>
        ) : null}
      </StyledSwipeableDrawer>
    </div>
  );
}
